import { useCallback, useState, useEffect } from 'react'
import { isEqual } from 'date-fns'
import { Fetch } from 'customer/function/Fetch'
import { format } from 'date-fns'
import type { FairEvent, BridalFairDate, RawBridalFairParticularDetail } from '../../BridalFairs.types'

export const useSearch = ({
  pageKey,
  initialDate,
}: {
  pageKey: string
  initialDate: Date | undefined
}): {
  onChange: (date: Date) => void
  selectedDate: Date | undefined
  bridalFairDates: BridalFairDate[]
} => {
  const [date, setDate] = useState<Date | undefined>(undefined)
  const onChange = useCallback(
    (date: Date) => {
      setDate(date)
    },
    [setDate],
  )
  const [bridalFairDates, setBridalFairDates] = useState<BridalFairDate[]>([])

  useEffect(() => {
    setDate(initialDate)
  }, [initialDate])

  useEffect(() => {
    ;(async () => {
      const formattedDate = date ? format(date, 'yyyyMMdd') : undefined
      if (!formattedDate) {
        setBridalFairDates([])
        return
      }
      const baseUrl = `/customer/api/v1/bridal_fair_pages/${pageKey}/bridal_fair_particular_details`
      const url = `${baseUrl}?start_date=${formattedDate}&end_date=${formattedDate}`
      const res = await Fetch(url, 'GET', {})
      const json = (await res.json()) as {
        bridalFairParticularDetails: RawBridalFairParticularDetail[]
      }
      setBridalFairDates(toBridalFairDates(json.bridalFairParticularDetails))
    })()
  }, [pageKey, date])

  return {
    onChange,
    selectedDate: date,
    bridalFairDates,
  }
}

const toBridalFairDates = (bridalFairParticularDetails: RawBridalFairParticularDetail[]): BridalFairDate[] => {
  return bridalFairParticularDetails.reduce((acc, bridalFairParticularDetail) => {
    if (!acc.some((date) => isEqual(date.date, new Date(bridalFairParticularDetail.particularDate)))) {
      acc.push({
        date: new Date(bridalFairParticularDetail.particularDate),
        fairEvents: [],
      })
    }
    const fairEvent: FairEvent = {
      bridalFairDetailKey: bridalFairParticularDetail.bridalFairDetail.key,
      isEdited: bridalFairParticularDetail.isIndividuallyEdited,
      isNetReservable: bridalFairParticularDetail.isNetReservable,
      name: bridalFairParticularDetail.name,
      mainImageUrl: bridalFairParticularDetail.imageUrl,
      mainImageData: bridalFairParticularDetail.imageData,
      descriptionText: bridalFairParticularDetail.descriptionText,
      tags: bridalFairParticularDetail.bridalFairDetail.fairTags.map((tag) => {
        return tag.name
      }),
      contents: bridalFairParticularDetail.bridalFairDetail.fairContents.map((content) => {
        return content.name
      }),
      withSpecialOffers: {
        forVisitor: bridalFairParticularDetail.bridalFairDetail.bridalSpecialOffers.some(
          (offer) => offer.applicableType === 'for_visitors',
        ),
        forSuccessCustomer: bridalFairParticularDetail.bridalFairDetail.bridalSpecialOffers.some(
          (offer) => offer.applicableType === 'for_success_customer',
        ),
      },
      minutes: bridalFairParticularDetail.bridalFair.minutes,
      fairTimes: bridalFairParticularDetail.fairTimes.map((fairTime) => ({
        startAt: new Date(fairTime.startAt),
        availability: fairTime.availability,
      })),
      pricing: bridalFairParticularDetail.pricing,
    }
    const dateIndex = acc.findIndex((date) => isEqual(date.date, new Date(bridalFairParticularDetail.particularDate)))
    acc[dateIndex].fairEvents.push(fairEvent)
    return acc
  }, [] as BridalFairDate[])
}
